import { useCallback } from "react";
import ModelColorItemInterface from "../../../../interfaces/model/ModelColorItemInterface";
import { FilterColorInterface } from "../../../../interfaces/catalog/FilterColorInterface";
import { FilterSuperCategoryInterface } from "../../../../interfaces/catalog/FilterSuperCategoryInterface";
import ModelCategoryInterface from "../../../../interfaces/model/ModelCategoryInterface";
import { SIZE_ORDER } from "../../../../Config";

export const useCatalogFilters = (
    modelColorItems: ModelColorItemInterface[], 
    superCategories?: FilterSuperCategoryInterface[],
    selectedColors?: FilterColorInterface[],
    setSelectedColors?: (colors: FilterColorInterface[]) => void,
    selectedSizes?: string[],
    setSelectedSizes?: (sizes: string[]) => void,
    selectedSuperCategories?: FilterSuperCategoryInterface[],
    setSelectedSuperCategories?: (categories: FilterSuperCategoryInterface[]) => void,
    selectedCategories?: ModelCategoryInterface[],
    setSelectedCategories?: (categories: ModelCategoryInterface[]) => void,
    selectedProperties?: Map<number, string[]>,
    setSelectedProperties?: (properties: Map<number, string[]>) => void
) => {
    const getUniqueColors = useCallback((): FilterColorInterface[] => {
        let uniqueColors = new Map();

        modelColorItems.map(colorItem => {
            if (colorItem.color_human_name && colorItem.hexcolor && colorItem.hexcolor.length === 7) {
                uniqueColors.set(colorItem.color_human_name, {
                    'color': colorItem.color_human_name,
                    'hex': colorItem.hexcolor
                })
            }
            return colorItem

        })

        return Array.from(uniqueColors.values())
    }, [modelColorItems])

    const getUniqueSizes = useCallback(() => {
        const uniqueSizes = modelColorItems.map(colorItem => Object.keys(colorItem.stocks)).flat()
            .filter((size, index, sizes) => sizes.indexOf(size) === index)

        const uniqueSorteredSize = SIZE_ORDER.split('|').filter(size => uniqueSizes.indexOf(size) > -1)

        return uniqueSorteredSize
    }, [modelColorItems])

    const getFilteredSuperCategories = useCallback((): FilterSuperCategoryInterface[] => {
        if (!superCategories) {
            return []
        }

        let uniqueSuperCategoryIdInModels: number[] = [];

        modelColorItems.map(colorItem => {
            uniqueSuperCategoryIdInModels.push(colorItem.super_category_id)
        })

        return superCategories.filter(superCategory => {
            let exist = false;
            superCategory.id.map(sid => {
                if (uniqueSuperCategoryIdInModels.indexOf(sid) !== -1) {
                    exist = true
                }
            })
            return exist
        })
    }, [modelColorItems, superCategories])

    const getInitPriceRange = () => {
        if (!modelColorItems.length) {
            return {
                min: 0,
                max: 0
            }
        }

        const uniquePrices = modelColorItems.map(colorItem => colorItem.min_price.amount)

        return {
            min: Math.min.apply(null, uniquePrices) / 100,
            max: Math.max.apply(null, uniquePrices) / 100
        }
    }

    const addColor = (inColor: FilterColorInterface) => {
        if (!selectedColors || !setSelectedColors) return;
        
        let uniqueColors = new Map(selectedColors.map(color => [color.color, color]));
        uniqueColors.set(inColor.color, inColor)
        setSelectedColors(Array.from(uniqueColors).map(([_, value]) => value))
    }

    const removeColor = (inColor: FilterColorInterface) => {
        if (!selectedColors || !setSelectedColors) return;
        
        setSelectedColors(selectedColors && selectedColors.filter(color => color.color !== inColor.color))
    }

    const addSize = (size: string) => {
        if (!selectedSizes || !setSelectedSizes) return;
        
        setSelectedSizes([...selectedSizes, ...[size]].filter((size, index, sizes) => sizes.indexOf(size) === index))
    }

    const removeSize = (inSize: string) => {
        if (!selectedSizes || !setSelectedSizes) return;
        
        setSelectedSizes(selectedSizes?.filter(size => size !== inSize) || [])
    }

    const addSuperCategory = (inSuperCategory: FilterSuperCategoryInterface) => {
        if (!selectedSuperCategories || !setSelectedSuperCategories) return;
        
        let uniqueSuperCategories = new Map(selectedSuperCategories.map(color => [color.name, color]));
        uniqueSuperCategories.set(inSuperCategory.name, inSuperCategory)
        setSelectedSuperCategories(Array.from(uniqueSuperCategories).map(([_, value]) => value))
    }

    const removeSuperCategory = (inSuperCategory: FilterSuperCategoryInterface) => {
        if (!selectedSuperCategories || !setSelectedSuperCategories) return;
        
        setSelectedSuperCategories(selectedSuperCategories.filter(superCategory => superCategory.name !== inSuperCategory.name))
    }

    const addCategory = (inCategory: ModelCategoryInterface) => {
        if (!selectedCategories || !setSelectedCategories) return;
        
        let uniqueCategories = new Map(selectedCategories.map(category => [category.name, category]));
        uniqueCategories.set(inCategory.name, inCategory)
        setSelectedCategories(Array.from(uniqueCategories).map(([_, value]) => value))
    }

    const removeCategory = (inCategory: ModelCategoryInterface) => {
        if (!selectedCategories || !setSelectedCategories) return;
        
        setSelectedCategories(selectedCategories.filter(category => category.name !== inCategory.name))
    }



    const addProperty = (id: number, value: string) => {
        if (!selectedProperties || !setSelectedProperties) return;
        
        let values = selectedProperties.get(id) || []
        values.push(value)
        selectedProperties.set(id, values)
        setSelectedProperties(new Map(selectedProperties))
    }

    const setProperties = (id: number, values: string[]) => {
        if (!selectedProperties || !setSelectedProperties) return;
        
        selectedProperties.set(id, values)
        setSelectedProperties(new Map(selectedProperties))
    }

    const removeProperty = (id: number, value: string) => {
        if (!selectedProperties || !setSelectedProperties) return;
        
        let values = selectedProperties.get(id) || []
        let nvalues = values.filter(svalue => svalue !== value)
        selectedProperties.set(id, nvalues)
        setSelectedProperties(new Map(selectedProperties))
    }

    return {
        getUniqueColors,
        getUniqueSizes,
        getFilteredSuperCategories,
        getInitPriceRange,
        addColor,
        removeColor,
        addSize,
        removeSize,
        addSuperCategory,
        removeSuperCategory,
        addCategory,
        removeCategory,
        addProperty,
        setProperties,
        removeProperty
    }
}